import { useState } from "react";
import { BrandLogo } from "@/components/BrandLogo";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { ForgotPasswordForm } from "@/components/auth/ForgotPasswordForm";
import { useAuth } from "@/context/AuthContext";
import "./Login.css";

const HERO = "https://images.unsplash.com/photo-1533923156502-be31530547c4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const GOOGLE_ICON = (
  <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"/></svg>
);

const T = {
  el: {
    heroTag: "Pregame only · No live data",
    heroTitle: <>Every line,<br />priced before<br />tip-off.</>,
    login: "Σύνδεση", register: "Εγγραφή",
    name: "Όνομα", username: "Όνομα χρήστη / Email",
    password: "Κωδικός πρόσβασης", passwordMin: "Κωδικός πρόσβασης (min 8)",
    confirm: "Επιβεβαίωση κωδικού",
    remember: "Να με θυμάσαι", forgot: "Ξέχασες τον κωδικό σου;",
    createAccount: "Δημιουργία λογαριασμού", signIn: "Είσοδος",
    orWith: "Ή συνδέσου με", soon: "σύντομα",
    haveAccount: "Έχεις ήδη λογαριασμό;", noAccount: "Δεν έχεις λογαριασμό;",
    goLogin: "Σύνδεση", goRegister: "Εγγραφή τώρα",
    privacy: "Πολιτική Απορρήτου",
    mismatch: "Οι κωδικοί δεν ταιριάζουν",
    googleFailed: "Η σύνδεση με Google απέτυχε. Προσπάθησε ξανά.",
    socialFailed: "Η σύνδεση απέτυχε ή ακυρώθηκε. Προσπάθησε ξανά.",
    socialNoEmail: "Δεν λάβαμε email από τον λογαριασμό σου. Χρειάζεται email για σύνδεση.",
    comingSoon: (n) => `Η σύνδεση με ${n} θα είναι διαθέσιμη σύντομα.`,
  },
  en: {
    heroTag: "Pregame only · No live data",
    heroTitle: <>Every line,<br />priced before<br />tip-off.</>,
    login: "Sign in", register: "Sign up",
    name: "Name", username: "Username / Email",
    password: "Password", passwordMin: "Password (min 8)",
    confirm: "Confirm password",
    remember: "Remember me", forgot: "Forgot your password?",
    createAccount: "Create account", signIn: "Sign in",
    orWith: "Or continue with", soon: "soon",
    haveAccount: "Already have an account?", noAccount: "Don't have an account?",
    goLogin: "Sign in", goRegister: "Sign up now",
    privacy: "Privacy Policy",
    mismatch: "Passwords don't match",
    googleFailed: "Google sign-in failed. Please try again.",
    socialFailed: "Sign-in failed or was cancelled. Please try again.",
    socialNoEmail: "We didn't get an email from your account. An email is required to sign in.",
    comingSoon: (n) => `Sign-in with ${n} will be available soon.`,
  },
};

export default function Login() {
  const { user, login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [lang, setLang] = useState(() => {
    const saved = localStorage.getItem("login_lang");
    if (saved === "en" || saved === "el") return saved;
    const nav = (navigator.language || navigator.userLanguage || "").toLowerCase();
    return nav.startsWith("el") ? "el" : "en";
  });
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [remember, setRemember] = useState(true);
  const [googleFailed] = useState(() => new URLSearchParams(window.location.search).get("google") === "failed");
  const [socialErr] = useState(() => new URLSearchParams(window.location.search).get("social"));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const isRegister = mode === "register";
  const t = T[lang];

  if (user) return <Navigate to="/" replace />;

  const setLanguage = (l) => { setLang(l); localStorage.setItem("login_lang", l); };

  const googleSignIn = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  const facebookSignIn = () => {
    window.location.href = `${process.env.REACT_APP_BACKEND_URL}/api/auth/facebook/start`;
  };
  const comingSoon = (n) => toast.info(t.comingSoon(n));

  const submit = async (e) => {
    e.preventDefault();
    if (isRegister && password !== confirm) { setError(t.mismatch); return; }
    setBusy(true);
    const err = isRegister ? await register(name, email, password) : await login(email, password, remember);
    setBusy(false);
    if (err) setError(err);
    else navigate("/");
  };

  const displayError = error || (socialErr === "noemail" ? t.socialNoEmail : socialErr ? t.socialFailed : googleFailed ? t.googleFailed : "");

  return (
    <div className="min-h-screen grid lg:grid-cols-[1.1fr_1fr]">
      <div className="relative hidden lg:block overflow-hidden">
        <img src={HERO} alt="Basketball arena" className="absolute inset-0 h-full w-full object-cover opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0B0E14]/40 to-[#0B0E14]" />
        <div className="relative h-full flex flex-col justify-end p-14">
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-emerald-400">{t.heroTag}</p>
          <h2 className="font-display text-6xl font-black uppercase leading-[0.9] mt-3">{t.heroTitle}</h2>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12 court-grain">
        {mode === "forgot" ? (
          <div className="w-full max-w-sm fade-up"><ForgotPasswordForm initialEmail={email} onBack={() => setMode("login")} /></div>
        ) : (
          <div className="lv7-scope fade-up">
            <div className="flex items-center justify-between mb-5">
              <BrandLogo />
              <div className="lv7-lang" role="group" aria-label="Language">
                <button type="button" className={lang === "el" ? "active" : ""} onClick={() => setLanguage("el")} data-testid="lang-el-btn">ΕΛ</button>
                <button type="button" className={lang === "en" ? "active" : ""} onClick={() => setLanguage("en")} data-testid="lang-en-btn">EN</button>
              </div>
            </div>
            <div className="lv7-box">
              <h2 data-testid="auth-title">{isRegister ? t.register : t.login}</h2>

              <form onSubmit={submit} data-testid="login-form">
                {isRegister && (
                  <div className="lv7-group">
                    <input type="text" id="name" required placeholder=" " maxLength={60} autoComplete="name"
                      value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name-input" />
                    <label htmlFor="name">{t.name}</label>
                  </div>
                )}

                <div className="lv7-group">
                  <input type="email" id="username" required placeholder=" " autoComplete="username"
                    value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email-input" />
                  <label htmlFor="username">{t.username}</label>
                </div>

                <div className="lv7-group">
                  <input type="password" id="password" required placeholder=" " minLength={isRegister ? 8 : undefined}
                    autoComplete={isRegister ? "new-password" : "current-password"}
                    value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password-input" />
                  <label htmlFor="password">{isRegister ? t.passwordMin : t.password}</label>
                </div>

                {isRegister && (
                  <div className="lv7-group">
                    <input type="password" id="confirm" required placeholder=" " autoComplete="new-password"
                      value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="register-confirm-input" />
                    <label htmlFor="confirm">{t.confirm}</label>
                  </div>
                )}

                {!isRegister && (
                  <div className="lv7-actions">
                    <label className="lv7-remember">
                      <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} data-testid="remember-me" /> {t.remember}
                    </label>
                    <button type="button" className="lv7-forgot" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); }}>{t.forgot}</button>
                  </div>
                )}

                {displayError && <p className="lv7-error" data-testid="login-error">{displayError}</p>}

                <button type="submit" className="lv7-submit" disabled={busy} data-testid="login-submit-btn">
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : isRegister ? t.createAccount : t.signIn}
                </button>
              </form>

              <div className="lv7-sep"><span>{t.orWith}</span></div>

              <div className="lv7-social">
                <button type="button" className="lv7-social-btn lv7-google" onClick={googleSignIn} data-testid="google-login-btn">
                  {GOOGLE_ICON} Google
                </button>
                <button type="button" className="lv7-social-btn lv7-facebook" onClick={facebookSignIn} data-testid="facebook-login-btn">
                  Facebook
                </button>
                <button type="button" className="lv7-social-btn lv7-twitter soon" onClick={() => comingSoon("X / Twitter")} data-testid="twitter-login-btn" title={t.soon}>
                  X / Twitter <span className="lv7-soon-tag">{t.soon}</span>
                </button>
              </div>

              <div className="lv7-signup">
                {isRegister ? t.haveAccount : t.noAccount}{" "}
                <button type="button" data-testid="auth-mode-toggle" onClick={() => { setMode(isRegister ? "login" : "register"); setError(""); }}>
                  {isRegister ? t.goLogin : t.goRegister}
                </button>
              </div>

              <div className="lv7-signup" style={{ marginTop: 10 }}>
                <Link to="/privacy" data-testid="privacy-link" style={{ color: "#8aa0c0", fontSize: 12.5 }}>{t.privacy}</Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
